import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import './DemoRequestListPage.css';

const STATUS_OPTIONS = [
  'New', 'Under Review', 'Qualified', 'Contacted',
  'Meeting Scheduled', 'Not a Fit', 'Closed',
];

const INDUSTRY_OPTIONS = [
  'Healthcare', 'Insurance', 'Financial Services', 'Banking',
  'Logistics / Supply Chain', 'Life Sciences', 'Manufacturing',
  'Retail', 'Technology', 'Government / Public Sector', 'Other',
];

const SIZE_OPTIONS = [
  '1–50', '51–200', '201–1,000', '1,001–5,000', '5,001–20,000', '20,000+',
];

const TIMELINE_OPTIONS = [
  'Immediately', 'Within 30 days', 'Within 90 days',
  'This quarter', 'Exploring / future planning',
];

const STATUS_COLORS = {
  'New': '#3B82F6',
  'Under Review': '#F59E0B',
  'Qualified': '#10B981',
  'Contacted': '#8B5CF6',
  'Meeting Scheduled': '#06B6D4',
  'Not a Fit': '#6B7280',
  'Closed': '#374151',
};

const PAGE_SIZE = 20;

export default function DemoRequestListPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);

  const [filters, setFilters] = useState({
    search: '', status: '', industry: '', company_size: '',
    timeline: '', assigned_to: '',
  });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.industry) params.industry = filters.industry;
      if (filters.company_size) params.company_size = filters.company_size;
      if (filters.timeline) params.timeline = filters.timeline;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;

      const data = await apiService.listDemoRequests(params);
      setRequests(data.requests || []);
      setTotalCount(data.totalCount || 0);
      setError('');
    } catch (err) {
      console.error('Failed to load demo requests:', err);
      const status = err?.response?.status;
      if (status === 401) {
        setError('Session expired. Please log in again.');
      } else if (status === 403) {
        setError('Access denied. Admin or CompanyAdmin role required.');
      } else {
        setError('Failed to load demo requests.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '', industry: '', company_size: '', timeline: '', assigned_to: '' });
    setPage(0);
  };

  const handleExport = async () => {
    try {
      const blob = await apiService.exportDemoRequests(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `demo_requests_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const isHighPriority = (req) => {
    const urgentTimelines = ['Immediately', 'Within 30 days'];
    const largeSizes = ['5,001–20,000', '20,000+'];
    const highInterest = ['Beta program', 'Pilot discussion'];
    const interest = req.interest_type || [];
    return (
      urgentTimelines.includes(req.timeline) ||
      largeSizes.includes(req.company_size) ||
      interest.some(i => highInterest.includes(i))
    );
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="drl">
      <div className="drl-header">
        <div>
          <h1 className="drl-header__title">Demo Requests</h1>
          <p className="drl-header__subtitle">{totalCount} total submissions</p>
        </div>
        <button className="drl-btn--export" onClick={handleExport}>Export CSV</button>
      </div>

      {error && <div className="drl-error">{error}</div>}

      {/* Filters */}
      <div className="drl-filters">
        <input
          className="drl-search"
          type="text" placeholder="Search name, company, email, phone..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
        />
        <select className="drl-filter-select" value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="drl-filter-select" value={filters.industry}
          onChange={(e) => handleFilterChange('industry', e.target.value)}>
          <option value="">All Industries</option>
          {INDUSTRY_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <select className="drl-filter-select" value={filters.company_size}
          onChange={(e) => handleFilterChange('company_size', e.target.value)}>
          <option value="">All Sizes</option>
          {SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="drl-filter-select" value={filters.timeline}
          onChange={(e) => handleFilterChange('timeline', e.target.value)}>
          <option value="">All Timelines</option>
          {TIMELINE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button className="drl-btn--clear" onClick={clearFilters}>Clear</button>
      </div>

      {/* Table */}
      <div className="drl-table-wrap">
        <table className="drl-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Company</th>
              <th>Title</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Industry</th>
              <th>Size</th>
              <th>Timeline</th>
              <th>Interest</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="drl-loading">Loading...</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan={11} className="drl-empty">No demo requests found.</td></tr>
            ) : (
              requests.map((req) => (
                <tr key={req.id}
                  className={`drl-row ${isHighPriority(req) ? 'drl-row--priority' : ''} ${req.is_flagged ? 'drl-row--flagged' : ''}`}
                  onClick={() => navigate(`/system/demo-requests/${req.id}`)}
                >
                  <td className="drl-cell--date">{formatDate(req.created_at)}</td>
                  <td>
                    <div className="drl-cell--name">
                      <strong>{req.first_name} {req.last_name}</strong>
                      {req.is_duplicate && <span className="drl-badge drl-badge--dup">DUP</span>}
                    </div>
                  </td>
                  <td>{req.company_name}</td>
                  <td className="drl-cell--muted">{req.job_title}</td>
                  <td className="drl-cell--muted">{req.work_email}</td>
                  <td className="drl-cell--muted">{req.phone_raw}</td>
                  <td>{req.industry}</td>
                  <td>{req.company_size}</td>
                  <td>
                    <span className={`drl-badge ${['Immediately', 'Within 30 days'].includes(req.timeline) ? 'drl-badge--urgent' : ''}`}>
                      {req.timeline}
                    </span>
                  </td>
                  <td>
                    <div className="drl-interest-tags">
                      {(Array.isArray(req.interest_type) ? req.interest_type : []).map(i => (
                        <span key={i} className="drl-tag">{i}</span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span className="drl-status" style={{ borderColor: STATUS_COLORS[req.status] || '#6B7280', color: STATUS_COLORS[req.status] || '#6B7280' }}>
                      {req.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="drl-pagination">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
